import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import i18n from '@/i18n';
import { getProductId } from '@/infrastructure/product-info';
import { getCompareDateValue, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import {
  FormEntry,
  FormType, IFormRef,
  QueryForm,
} from '@/presentation/components/form';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getVersionLabel,
  onChangeAppVersion,
  getShowSwitchStatus,
} from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { numberFilterTips } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: FieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

/**
 * vc泄漏-问题列表>查询表单
 */

export const ProblemForm: FC<IProps> = observer((props) => {
  const { tokenInitValues } = props;
  const [showSwitch, setShowSwitch] = useState(false);
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const config = useAppConfig();

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.MemoryVcLeakDetail || props.entry === FilterEntry.MemoryVcLeakList,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: props.entry !== FilterEntry.MemoryVcLeakList,
      isIssuesTimeWindow: props.entry === FilterEntry.MemoryVcLeakList,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.VcLeak,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.VcLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldScope: Zone.VcLeak,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.VcLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.AutoComplete,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.VcLeak,
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: i18n.t('用户ID'),
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: i18n.t('设备ID'),
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: i18n.t('启动ID'),
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: i18n.t('消息ID'),
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.MemoryVcLeakList,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: i18n.t('问题特征'),
      group: FormColumnGroup.exceptionInfo,
      disabled: props.entry !== FilterEntry.MemoryVcLeakList,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      group: FormColumnGroup.DeviceInfo,
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.VcLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
    },
    {
      type: FormType.Select,
      name: FieldKey.ShiplyTag,
      group: FormColumnGroup.Custom,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.VcLeak,
      mode: 'tags',
      disabled: !config.enableShiplyAbTestTag,
    },
    {
      type: FormType.Select,
      name: FieldKey.AbTestTag,
      group: FormColumnGroup.Custom,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.VcLeak,
      mode: 'tags',
    },
    {
      type: FormType.Select,
      name: FieldKey.TagsInFilter,
      group: FormColumnGroup.Tag,
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      initialValue: [FIELD_ALL],
      fieldScope: Zone.VcLeak,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      compareDisabled: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      label: hintText(i18n.t('使用时长（秒）'), numberFilterTips),
      labelString: i18n.t('使用时长（秒）'),
      options: NUMBER_MATCH_OPTIONS,
      group: FormColumnGroup.ContextInfo,
    },
    // {
    //   type: FormType.Input,
    //   name: FieldKey.AbTestTag,
    //   label: '实验标签',
    //   initialValue: FIELD_ALL,
    // },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: i18n.t('地域'),
      labelString: i18n.t('地域'),
      fieldScope: Zone.VcLeak,
      compareDisabled: true,
      initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: i18n.t('自定义维度'),
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      label: i18n.t('自定义字段'),
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: i18n.t('只显示新增问题'),
      disabled: !showSwitch || props.entry !== FilterEntry.MemoryVcLeakList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.VcLeak,
      initialValue: false,
      compareDisabled: true,
    },
  ];

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    props.onFinish({ ...value, ...compareDateValue });
  };

  return <QueryForm
    form={form}
    ref={tableRef}
    cachePrefix={'prefix1'}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_MEM_LEAK}
    hasCompare={props.entry === FilterEntry.MemoryVcLeakList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
});
